import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, Modal, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Pagination, CircularProgress, Alert, TextField, Select, MenuItem, FormControl } from '@mui/material';
import { useTheme } from '@mui/material';
import { tokens } from '../theme';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import CloseIcon from '@mui/icons-material/Close';
import Header from '../components/Header';
import { getSellLeads } from '../services/propertyService';
import { API_BASE_URL } from '../config/api';
import EnhancedSearchBar from '../components/EnhancedSearchBar';
import FullSizeImageViewer from '../components/FullSizeImageViewer';

const SellPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // State for API data and loading
    const [sellData, setSellData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Pagination state
    const [page, setPage] = useState(1);
    const [pageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalRecords, setTotalRecords] = useState(0);

    // Modal state
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [isViewModalOpen, setIsViewModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editFormData, setEditFormData] = useState(null);
    const [selectedImages, setSelectedImages] = useState([]);
    const [imagePreviews, setImagePreviews] = useState([]);

    // Add state for image carousel
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Add new state for search queries
    const [idSearchQuery, setIdSearchQuery] = useState('');
    const [citySearchQuery, setCitySearchQuery] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    // Add new state for full size image viewer
    const [isFullSizeViewerOpen, setIsFullSizeViewerOpen] = useState(false);
    const [selectedImageUrl, setSelectedImageUrl] = useState(null);

    // Fetch data from API
    const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await getSellLeads(page, pageSize, fromDate || null, toDate || null, idSearchQuery || null, citySearchQuery || null);

            console.log('Sell API Response:', {
                success: response.success,
                dataCount: response.result?.data?.length || 0,
                totalRecords: response.result?.totalRecords || 0,
                fromDate,
                toDate,
                idSearchQuery,
                citySearchQuery
            });

            if (response.success) {
                const newData = response.result.data || [];
                const totalRecords = response.result.totalRecords || 0;

                setSellData(newData);
                setTotalPages(response.result.totalPages || 1);
                setTotalRecords(totalRecords);
            } else {
                // API returned unsuccessful response
                setSellData([]);
                setTotalPages(1);
                setTotalRecords(0);
                setError(response.message || 'Failed to fetch properties');
            }
        } catch (err) {
            console.error('Error fetching sell data:', err);
            // Clear data on error
            setSellData([]);
            setTotalPages(1);
            setTotalRecords(0);
            setError('An error occurred while fetching data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [page, pageSize, fromDate, toDate, idSearchQuery, citySearchQuery]);

    // Handle page change
    const handlePageChange = (event, value) => {
        setPage(value);
    };

    // Open view modal
    const openViewModal = (property) => {
        setSelectedProperty(property);
        setCurrentImageIndex(0);
        setIsViewModalOpen(true);
    };

    // Close view modal
    const closeViewModal = () => {
        setIsViewModalOpen(false);
        setSelectedProperty(null);
    };

    // Format price with commas and decimals
    const formatPrice = (price) => {
        if (price === null || price === undefined || isNaN(Number(price))) return 'N/A';
        return Number(price).toLocaleString('en-US', {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2
        });
    };

    // Format area based on property type
    const formatArea = (property) => {
        const units = [];
        if (property.Acre) units.push(`${property.Acre} Acre${property.Acre > 1 ? 's' : ''}`);
        if (property.Kanal) units.push(`${property.Kanal} Kanal${property.Kanal > 1 ? 's' : ''}`);
        if (property.Marla) units.push(`${property.Marla} Marla${property.Marla > 1 ? 's' : ''}`);
        if (property.Yard) units.push(`${property.Yard} Yard${property.Yard > 1 ? 's' : ''}`);
        if (property.Square_ft) units.push(`${property.Square_ft} Square Ft`);
        return units.length > 0 ? units.join(', ') : 'N/A';
    };

    // Handle download reports
    const handleDownloadReports = () => {
        try {
            // Create CSV content
            const headers = ['Property ID', 'Property Type', 'Property Title', 'Location', 'Price', 'Dealer Name', 'Phone', 'Expiry Date', 'Status'];

            let csvContent = headers.join(',') + '\n';

            sellData.forEach(item => {
                const row = [
                    `"${item.Property_ID || ''}"`,
                    `"${item.Property_Type || ''}"`,
                    `"${item.Property_Title?.replace(/"/g, '""') || ''}"`,
                    `"${item.City || ''}"`,
                    `"${item.Property_Price || 0}"`,
                    `"${item.Dealer_Name?.replace(/"/g, '""') || ''}"`,
                    `"${item.Dealer_Phone_No || ''}"`,
                    `"${item.Expiry_Date ? new Date(item.Expiry_Date).toLocaleDateString() : ''}"`,
                    `"${item.Status || ''}"`,
                ];
                csvContent += row.join(',') + '\n';
            });

            // Create a timestamp for the filename
            const date = new Date();
            const timestamp = date.toISOString().replace(/[:.]/g, '-').slice(0, 19);
            const filename = `properties_for_sale_${timestamp}.csv`;

            // Create a blob and download link
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', filename);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error("Error generating CSV report:", error);
            alert("There was an error generating the report. Please try again.");
        }
    };

    // Process image URL to ensure it's complete
    const getImageUrl = (imagePath) => {
        if (!imagePath) return null;

        console.log("imagePath test", imagePath);

        // Format the path to access images from server
        // The path is typically in format 'property_images/image_filename.jpg'
        return `${API_BASE_URL}/uploads/${imagePath}`;
    };

    // Check if file is an image
    const isImage = (path) => {
        if (!path) return false;
        const ext = path.split('.').pop().toLowerCase();
        return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
    };

    // Check if file is a video
    const isVideo = (path) => {
        if (!path) return false;
        const ext = path.split('.').pop().toLowerCase();
        return ['mp4', 'webm', 'ogg', 'mov'].includes(ext);
    };

    // Get all media (images and videos) from property data
    const getPropertyMedia = (property) => {
        if (!property) return { images: [], videos: [] };

        let mediaArray = [];

        // Check if images exists and is an array
        if (property.images && Array.isArray(property.images)) {
            mediaArray = property.images;
        }
        // Check if images is a string (might be JSON)
        else if (property.images && typeof property.images === 'string') {
            try {
                const parsed = JSON.parse(property.images);
                mediaArray = Array.isArray(parsed) ? parsed : [];
            } catch (e) {
                // Not valid JSON, might be a comma-separated string
                mediaArray = property.images.split(',').map(img => img.trim()).filter(img => img);
            }
        }

        // Filter media into images and videos
        const images = mediaArray.filter(item => isImage(item));
        const videos = mediaArray.filter(item => isVideo(item));

        return { images, videos };
    };

    // Handle next image in carousel
    const handleNextImage = (e) => {
        e.stopPropagation();
        if (!selectedProperty) return;

        const { images } = getPropertyMedia(selectedProperty);
        if (images.length <= 1) return;

        setCurrentImageIndex((prev) => (prev + 1) % images.length);
    };

    // Handle previous image in carousel
    const handlePrevImage = (e) => {
        e.stopPropagation();
        if (!selectedProperty) return;

        const { images } = getPropertyMedia(selectedProperty);
        if (images.length <= 1) return;

        setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const handleImageChange = (e) => {
        const files = Array.from(e.target.files);
        const newImages = files.map(file => ({
            file,
            preview: URL.createObjectURL(file)
        }));

        setSelectedImages(prev => [...prev, ...newImages]);
        setImagePreviews(prev => [...prev, ...newImages.map(img => img.preview)]);
    };

    const removeImage = (index) => {
        setSelectedImages(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const handleEdit = (property) => {
        const existingImages = property.images?.map(image => ({
            file: null,
            preview: getImageUrl(image)
        })) || [];

        setSelectedImages(existingImages);
        setImagePreviews(existingImages.map(img => img.preview));

        // Helper function to safely get property value with fallbacks
        const getPropertyValue = (property, fieldNames, defaultValue = '') => {
            for (const fieldName of fieldNames) {
                if (property[fieldName] !== undefined && property[fieldName] !== null) {
                    return property[fieldName];
                }
            }
            return defaultValue;
        };

        setEditFormData({
            Property_ID: getPropertyValue(property, ['Property_ID', 'property_id', 'PropertyID']),
            User_ID: getPropertyValue(property, ['User_ID', 'user_id', 'UserID']),
            Property_Type: getPropertyValue(property, ['Property_Type', 'property_type', 'PropertyType']),
            Commercial_Type: getPropertyValue(property, ['Commercial_Type', 'commercial_type', 'CommercialType']),
            Building_Type: getPropertyValue(property, ['Building_Type', 'building_type', 'BuildingType']),
            City: getPropertyValue(property, ['City', 'city', 'Property_Location', 'property_location']),
            Property_Location: getPropertyValue(property, ['Property_Location', 'property_location', 'Address', 'address']),
            Marla: getPropertyValue(property, ['Marla', 'marla'], ''),
            Kanal: getPropertyValue(property, ['Kanal', 'kanal'], ''),
            Yard: getPropertyValue(property, ['Yard', 'yard'], ''),
            Property_Price: getPropertyValue(property, ['Property_Price', 'property_price', 'Price', 'price']),
            Property_Title: getPropertyValue(property, ['Property_Title', 'property_title', 'Title', 'title']),
            Property_Description: getPropertyValue(property, ['Property_Description', 'property_description', 'Description', 'description']),
            No_of_Floors: getPropertyValue(property, ['No_of_Floors', 'no_of_floors', 'NoOfFloors']),
            No_Of_Bathrooms: getPropertyValue(property, ['No_Of_Bathrooms', 'no_of_bathrooms', 'NoOfBathrooms', 'No_of_Bathrooms', 'no_of_bathrooms']),
            No_of_Beds: getPropertyValue(property, ['No_of_Beds', 'no_of_beds', 'NoOfBeds', 'No_of_Bed', 'no_of_bed']),
            No_of_Kitchens: getPropertyValue(property, ['No_of_Kitchens', 'no_of_kitchens', 'NoOfKitchens', 'No_of_Kitchen', 'no_of_kitchen']),
            Dealer_Name: getPropertyValue(property, ['Dealer_Name', 'dealer_name', 'Contact_Name', 'contact_name']),
            Dealer_Phone_No: getPropertyValue(property, ['Dealer_Phone_No', 'dealer_phone_no', 'Contact_Number', 'contact_number']),
            Expiry_Date: property.Expiry_Date ? property.Expiry_Date.split('T')[0] : '',
            Status: getPropertyValue(property, ['Status', 'status'], 'Available'),
            Square_ft: getPropertyValue(property, ['Square_ft', 'square_ft', 'SquareFt'], ''),
        });
        setIsEditModalOpen(true);
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            // Validate required fields
            if (!editFormData.City || !editFormData.Property_Type || !editFormData.Dealer_Phone_No) {
                setError('City, Property Type, and Dealer Phone Number are required fields');
                return;
            }

            const formData = new FormData();

            // Required fields - always append these
            formData.append('Property_ID', editFormData.Property_ID);
            formData.append('User_ID', editFormData.User_ID);
            formData.append('City', editFormData.City);
            formData.append('Property_Type', editFormData.Property_Type);
            formData.append('Dealer_Phone_No', editFormData.Dealer_Phone_No);

            // Optional fields - only append if they have actual values (not null, undefined, or empty string)
            const optionalFields = {
                'Commercial_Type': editFormData.Commercial_Type,
                'Building_Type': editFormData.Building_Type,
                'Property_Location': editFormData.Property_Location,
                'Marla': editFormData.Marla,
                'Kanal': editFormData.Kanal,
                'Yard': editFormData.Yard,
                'Property_Price': editFormData.Property_Price,
                'Property_Title': editFormData.Property_Title,
                'Property_Description': editFormData.Property_Description,
                'No_of_Floors': editFormData.No_of_Floors,
                'No_Of_Bathrooms': editFormData.No_Of_Bathrooms,
                'No_of_Beds': editFormData.No_of_Beds,
                'No_of_Kitchens': editFormData.No_of_Kitchens,
                'Dealer_Name': editFormData.Dealer_Name,
                'Expiry_Date': editFormData.Expiry_Date,
                'Status': editFormData.Status,
                'Square_ft': editFormData.Square_ft,
            };

            // Only append fields that have actual values
            Object.entries(optionalFields).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== '') {
                    formData.append(key, value);
                }
            });

            // Handle both existing and new images
            let imageIndex = 0;
            selectedImages.forEach((image) => {
                if (image.file) {
                    // New image file
                    formData.append(`Sell_Image[${imageIndex}][Image]`, image.file);
                    imageIndex++;
                } else if (image.preview) {
                    // Existing image - extract path from preview URL
                    const imagePath = image.preview.split('/uploads/')[1];
                    if (imagePath) {
                        formData.append(`Sell_Image[${imageIndex}][Image]`, imagePath);
                        imageIndex++;
                    }
                }
            });

            const response = await fetch(`${API_BASE_URL}/api/property/update-sellProperty`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                await fetchData();
                setIsEditModalOpen(false);
                setSelectedImages([]);
                setImagePreviews([]);
                setError(null); // Clear any previous errors
            } else {
                throw new Error('Failed to update property');
            }
        } catch (error) {
            console.error('Error updating property:', error);
            setError('Failed to update property');
        }
    };

    const modalStyle = {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: { xs: '90%', sm: '600px' },
        maxHeight: '90vh',
        overflow: 'auto',
        bgcolor: colors.primary[400],
        color: colors.grey[100],
        boxShadow: 24,
        p: 4,
        borderRadius: 2
    };

    // Remove frontend filtering - now handled by API
    // const filterData = (data) => {
    //     return data.filter(item => {
    //         const matchesId = !idSearchQuery ||
    //             (item.Unique_ID && item.Unique_ID.toLowerCase().includes(idSearchQuery.toLowerCase()));
    //         const matchesCity = !citySearchQuery ||
    //             (item.City && item.City.toLowerCase() === citySearchQuery.toLowerCase());
    //         return matchesId && matchesCity;
    //     });
    // };

    // Add new function to handle image click
    const handleImageClick = (imageUrl) => {
        setSelectedImageUrl(imageUrl);
        setIsFullSizeViewerOpen(true);
    };

    // Add new function to close full size viewer
    const handleCloseFullSizeViewer = () => {
        setIsFullSizeViewerOpen(false);
        setSelectedImageUrl(null);
    };

    return (
        <Box m="20px">
            <Header title="PROPERTIES FOR SALE" subtitle={`Manage Properties (${totalRecords} total)`} />

            {/* Enhanced Search Bar */}
            <EnhancedSearchBar
                onIdSearch={setIdSearchQuery}
                onCitySearch={setCitySearchQuery}
                onFromDateSearch={setFromDate}
                onToDateSearch={setToDate}
            />

            {/* Date Filter Indicator */}
            {(fromDate || toDate) && (
                <Box mb={2} p={2} sx={{ backgroundColor: '#E3F2FD', borderRadius: '4px', border: '1px solid #2196F3' }}>
                    <Typography variant="body2" color="#1976D2" fontWeight="bold">
                        🗓️ Date Filter Active:
                        {fromDate && ` From: ${fromDate}`}
                        {toDate && ` To: ${toDate}`}
                        {` (Showing ${totalRecords} results)`}
                    </Typography>
                </Box>
            )}

            {/* Download Reports Button */}
            <Box display="flex" justifyContent="flex-end" mb={2}>
                <Button
                    variant="contained"
                    startIcon={<DownloadOutlinedIcon />}
                    onClick={handleDownloadReports}
                    sx={{
                        backgroundColor: '#D32F2F',
                        '&:hover': {
                            backgroundColor: '#B71C1C'
                        }
                    }}
                >
                    Download Reports
                </Button>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" height="400px">
                    <CircularProgress />
                </Box>
            ) : sellData.length === 0 ? (
                <Box
                    display="flex"
                    flexDirection="column"
                    justifyContent="center"
                    alignItems="center"
                    height="400px"
                    sx={{ backgroundColor: '#FFFFFF', borderRadius: 1, p: 3 }}
                >
                    <Typography variant="h6" color="#757575" gutterBottom>
                        No Records Found
                    </Typography>
                    <Typography variant="body2" color="#9E9E9E" textAlign="center">
                        {idSearchQuery || citySearchQuery || fromDate || toDate
                            ? "No properties match your current search criteria. Try adjusting your filters."
                            : "No properties for sale available at the moment."
                        }
                    </Typography>
                </Box>
            ) : (
                <TableContainer
                    component={Paper}
                    sx={{
                        backgroundColor: '#FFFFFF',
                        overflowX: 'auto',
                        '& .MuiTable-root': {
                            minWidth: 650,
                        }
                    }}
                >
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>ID</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Image</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>City</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Area</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Price</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Property Type</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Expiry Date</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Status</TableCell>
                                <TableCell sx={{
                                    fontWeight: 'bold',
                                    backgroundColor: '#D32F2F',
                                    color: '#FFFFFF',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sellData.map((property) => (
                                <TableRow key={property.Property_ID}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{property.Unique_ID || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Box width="60px" height="60px" borderRadius="4px" overflow="hidden">
                                            {property.images && property.images[0] ? (
                                                <img
                                                    src={getImageUrl(property.images[0])}
                                                    alt="Property Preview"
                                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                                />
                                            ) : (
                                                <Box
                                                    width="100%"
                                                    height="100%"
                                                    display="flex"
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    backgroundColor="#F5F5F5"
                                                >
                                                    <Typography variant="body2" color="#757575" fontWeight="bold">
                                                        No Image
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {property.City || property.Property_Location || 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{formatArea(property)}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{formatPrice(property.Property_Price)}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>{property.Property_Type || 'N/A'}</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        {property.Expiry_Date ? new Date(property.Expiry_Date).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>
                                        <Typography
                                            sx={{
                                                color: property.Status === 'Available' ? '#4CAF50' :
                                                    property.Status === 'Rented' ? '#FF9800' :
                                                        property.Status === 'Sold' ? '#F44336' : '#757575',
                                                fontWeight: 'bold'
                                            }}
                                        >
                                            {property.Status || 'N/A'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Box display="flex" gap={1}>
                                            <IconButton
                                                onClick={() => openViewModal(property)}
                                                sx={{
                                                    color: '#000000',
                                                    '&:hover': {
                                                        backgroundColor: '#F5F5F5'
                                                    }
                                                }}
                                            >
                                                <VisibilityOutlinedIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => handleEdit(property)}
                                                sx={{
                                                    color: '#000000',
                                                    '&:hover': {
                                                        backgroundColor: '#F5F5F5'
                                                    }
                                                }}
                                            >
                                                <EditOutlinedIcon />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {!loading && (
                <Box display="flex" justifyContent="center" mt={2}>
                    <Pagination
                        count={Math.ceil(totalRecords / pageSize)}
                        page={page}
                        onChange={handlePageChange}
                        color="primary"
                        sx={{
                            '& .MuiPaginationItem-root': {
                                color: '#000000',
                                fontSize: '1rem',
                                fontWeight: 'bold',
                                '&:hover': {
                                    backgroundColor: '#F5F5F5'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-selected': {
                                backgroundColor: '#D32F2F',
                                color: '#FFFFFF',
                                '&:hover': {
                                    backgroundColor: '#B71C1C'
                                }
                            },
                            '& .MuiPaginationItem-root.Mui-disabled': {
                                color: '#9E9E9E'
                            }
                        }}
                    />
                </Box>
            )}

            {/* View Property Modal */}
            <Modal
                open={isViewModalOpen}
                onClose={closeViewModal}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Box
                    sx={{
                        position: 'relative',
                        width: '80%',
                        maxHeight: '90vh',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 4,
                        borderRadius: 2,
                        overflow: 'auto',
                    }}
                >
                    {selectedProperty && (
                        <>
                            <Typography variant="h6" component="h2" mb={2} color="#000000">
                                Property Details
                            </Typography>

                            {selectedProperty.images && selectedProperty.images.length > 0 && (
                                <Box position="relative" mb={3}>
                                    <img
                                        src={getImageUrl(selectedProperty.images[currentImageIndex])}
                                        alt={`Property ${currentImageIndex + 1}`}
                                        style={{
                                            width: '100%',
                                            height: '300px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                        }}
                                        onClick={() => handleImageClick(getImageUrl(selectedProperty.images[currentImageIndex]))}
                                    />
                                    {selectedProperty.images.length > 1 && (
                                        <>
                                            <IconButton
                                                onClick={() => setCurrentImageIndex(prev => (prev - 1 + selectedProperty.images.length) % selectedProperty.images.length)}
                                                sx={{
                                                    position: 'absolute',
                                                    left: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#FFFFFF',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.7)'
                                                    }
                                                }}
                                            >
                                                <NavigateBeforeIcon />
                                            </IconButton>
                                            <IconButton
                                                onClick={() => setCurrentImageIndex(prev => (prev + 1) % selectedProperty.images.length)}
                                                sx={{
                                                    position: 'absolute',
                                                    right: 8,
                                                    top: '50%',
                                                    transform: 'translateY(-50%)',
                                                    bgcolor: 'rgba(0,0,0,0.5)',
                                                    color: '#FFFFFF',
                                                    '&:hover': {
                                                        bgcolor: 'rgba(0,0,0,0.7)'
                                                    }
                                                }}
                                            >
                                                <NavigateNextIcon />
                                            </IconButton>
                                        </>
                                    )}
                                </Box>
                            )}

                            <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                                {Object.entries(selectedProperty).map(([key, value]) => {
                                    // Skip images array as it's handled separately
                                    if (key === 'images') return null;

                                    // Robust filter for unwanted fields
                                    const fieldsToSkip = ['latitude', 'longitude', 'irrigationtime', 'nooftubewel', 'no of tubewel'];
                                    const normalizedKey = key.replace(/[\s_]/g, '').toLowerCase();
                                    if (fieldsToSkip.includes(normalizedKey)) return null;

                                    // Format the key for display
                                    const displayKey = key
                                        .split('_')
                                        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
                                        .join(' ');

                                    // Format the value based on the field type
                                    let displayValue = value;
                                    if (key === 'Property_Price') {
                                        displayValue = formatPrice(value);
                                    } else if (key === 'Marla' || key === 'Kanal' || key === 'Acre' || key === 'Yard') {
                                        displayValue = value ? `${value} ${key}` : 'N/A';
                                    }

                                    return (
                                        <Box key={key}>
                                            <Typography variant="subtitle1" fontWeight="bold" color="#000000">
                                                {displayKey}
                                            </Typography>
                                            <Typography color="#000000">
                                                {displayValue || 'N/A'}
                                            </Typography>
                                        </Box>
                                    );
                                })}
                               
                            </Box>

                            <Box display="flex" justifyContent="flex-end" mt={3}>
                                <Button
                                    variant="contained"
                                    onClick={closeViewModal}
                                    sx={{
                                        backgroundColor: '#D32F2F',
                                        '&:hover': {
                                            backgroundColor: '#B71C1C'
                                        }
                                    }}
                                >
                                    Close
                                </Button>
                            </Box>
                        </>
                    )}
                </Box>
            </Modal>

            {/* Add Edit Modal */}
            <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '80%',
                    maxWidth: '800px',
                    bgcolor: '#FFFFFF',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: '8px',
                    maxHeight: '90vh',
                    overflow: 'auto'
                }}>
                    <Typography variant="h6" component="h2" mb={2} color="#000000">
                        Edit Property
                    </Typography>

                    <form onSubmit={handleEditSubmit}>
                        <Box display="grid" gridTemplateColumns="repeat(2, 1fr)" gap={2}>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property ID</Typography>
                                <Typography color="#000000">{editFormData?.Property_ID || 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">User ID</Typography>
                                <Typography color="#000000">{editFormData?.User_ID || 'N/A'}</Typography>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property Type</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        name="Property_Type"
                                        value={editFormData?.Property_Type || ''}
                                        onChange={handleEditFormChange}
                                        sx={{ color: '#000000' }}
                                    >
                                        <MenuItem value="House">House</MenuItem>
                                        <MenuItem value="Commercial">Commercial</MenuItem>
                                        <MenuItem value="Plot">Plot</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Commercial Type</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        name="Commercial_Type"
                                        value={editFormData?.Commercial_Type || ''}
                                        onChange={handleEditFormChange}
                                        sx={{ color: '#000000' }}
                                    >
                                        <MenuItem value="Plot">Plot</MenuItem>
                                        <MenuItem value="Buildings">Building</MenuItem>
                                        <MenuItem value="Shop">Shop</MenuItem>
                                        <MenuItem value="Flat">Flat</MenuItem>
                                        <MenuItem value="Office">Office</MenuItem>
                                        <MenuItem value="Pent House">Pent House</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                            {/* <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Building Type</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        name="Building_Type"
                                        value={editFormData?.Building_Type || ''}
                                        onChange={handleEditFormChange}
                                        sx={{ color: '#000000' }}
                                    >
                                        <MenuItem value="Shop">Shop</MenuItem>
                                        <MenuItem value="Flat">Flat</MenuItem>
                                        <MenuItem value="Office">Office</MenuItem>
                                        <MenuItem value="Pent Houses">Pent Houses</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box> */}
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">City</Typography>
                                <TextField
                                    fullWidth
                                    name="City"
                                    value={editFormData?.City || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property Location</Typography>
                                <TextField
                                    fullWidth
                                    name="Property_Location"
                                    value={editFormData?.Property_Location || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Marla</Typography>
                                <TextField
                                    fullWidth
                                    name="Marla"
                                    type="number"
                                    value={editFormData?.Marla || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Kanal</Typography>
                                <TextField
                                    fullWidth
                                    name="Kanal"
                                    type="number"
                                    value={editFormData?.Kanal || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Square Feet</Typography>
                                <TextField
                                    fullWidth
                                    name="Square_ft"
                                    type="number"
                                    value={editFormData?.Square_ft || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    placeholder="Enter area in square feet"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Yard</Typography>
                                <TextField
                                    fullWidth
                                    name="Yard"
                                    type="number"
                                    value={editFormData?.Yard || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property Price</Typography>
                                <TextField
                                    fullWidth
                                    name="Property_Price"
                                    type="number"
                                    value={editFormData?.Property_Price || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Property Title</Typography>
                                <TextField
                                    fullWidth
                                    name="Property_Title"
                                    value={editFormData?.Property_Title || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Number of Floors</Typography>
                                <TextField
                                    fullWidth
                                    name="No_of_Floors"
                                    type="number"
                                    value={editFormData?.No_of_Floors || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Number of Bathrooms</Typography>
                                <TextField
                                    fullWidth
                                    name="No_Of_Bathrooms"
                                    type="number"
                                    value={editFormData?.No_Of_Bathrooms || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Number of Beds</Typography>
                                <TextField
                                    fullWidth
                                    name="No_of_Beds"
                                    type="number"
                                    value={editFormData?.No_of_Beds || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Number of Kitchens</Typography>
                                <TextField
                                    fullWidth
                                    name="No_of_Kitchens"
                                    type="number"
                                    value={editFormData?.No_of_Kitchens || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Dealer Name</Typography>
                                <TextField
                                    fullWidth
                                    name="Dealer_Name"
                                    value={editFormData?.Dealer_Name || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Dealer Phone Number</Typography>
                                <TextField
                                    fullWidth
                                    name="Dealer_Phone_No"
                                    value={editFormData?.Dealer_Phone_No || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Expiry Date</Typography>
                                <TextField
                                    fullWidth
                                    name="Expiry_Date"
                                    type="date"
                                    value={editFormData?.Expiry_Date || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    size="small"
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                />
                            </Box>
                            <Box>
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000">Status</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        name="Status"
                                        value={editFormData?.Status || 'Available'}
                                        onChange={handleEditFormChange}
                                        sx={{ color: '#000000' }}
                                    >
                                        <MenuItem value="Available">Available</MenuItem>
                                        <MenuItem value="Rented">Rented</MenuItem>
                                        <MenuItem value="Sold">Sold</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>
                         
                            <Box gridColumn="span 2">
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000" mb={1}>Property Description</Typography>
                                <TextField
                                    fullWidth
                                    name="Property_Description"
                                    value={editFormData?.Property_Description || ''}
                                    onChange={handleEditFormChange}
                                    variant="outlined"
                                    multiline
                                    rows={4}
                                    sx={{ '& .MuiOutlinedInput-root': { color: '#000000' } }}
                                />
                            </Box>
                            <Box gridColumn="span 2">
                                <Typography variant="subtitle1" fontWeight="bold" color="#000000" mb={1}>Property Images</Typography>
                                <Box
                                    sx={{
                                        border: '2px dashed #ccc',
                                        borderRadius: '4px',
                                        p: 2,
                                        textAlign: 'center',
                                        cursor: 'pointer',
                                        '&:hover': {
                                            borderColor: '#000000'
                                        },
                                        mb: 2
                                    }}
                                    onClick={() => document.getElementById('image-upload').click()}
                                >
                                    <Typography color="#666666">
                                        Click to upload images (Multiple images allowed)
                                    </Typography>
                                    <input
                                        id="image-upload"
                                        type="file"
                                        accept="image/*"
                                        multiple
                                        onChange={handleImageChange}
                                        style={{ display: 'none' }}
                                    />
                                </Box>

                                {/* Image Preview Grid */}
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                        gap: 2,
                                        mt: 2
                                    }}
                                >
                                    {imagePreviews.map((preview, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                position: 'relative',
                                                width: '100%',
                                                paddingTop: '100%',
                                                borderRadius: '4px',
                                                overflow: 'hidden'
                                            }}
                                        >
                                            <img
                                                src={preview}
                                                alt={`Preview ${index + 1}`}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                            <IconButton
                                                onClick={() => removeImage(index)}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                                                    color: '#fff',
                                                    '&:hover': {
                                                        backgroundColor: 'rgba(0, 0, 0, 0.7)'
                                                    }
                                                }}
                                            >
                                                <CloseIcon />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Box>
                            </Box>
                        </Box>

                        <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
                            <Button
                                variant="outlined"
                                onClick={() => setIsEditModalOpen(false)}
                                sx={{
                                    color: '#000000',
                                    borderColor: '#000000',
                                    '&:hover': {
                                        borderColor: '#000000',
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                    }
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                sx={{
                                    backgroundColor: '#D32F2F',
                                    '&:hover': {
                                        backgroundColor: '#B71C1C'
                                    }
                                }}
                            >
                                Save Changes
                            </Button>
                        </Box>
                    </form>
                </Box>
            </Modal>

            {/* Add FullSizeImageViewer component */}
            <FullSizeImageViewer
                open={isFullSizeViewerOpen}
                onClose={handleCloseFullSizeViewer}
                imageUrl={selectedImageUrl}
            />
        </Box>
    );
};

export default SellPage;

